import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useOverlayContext, useIsInBottomSheet, useStateInRef, GridItem, TextButton } from 'seagull';
import ContactPicker from '~/components/ContactsPicker/ContactPicker';
import { ManageContactModalForVendor } from '~/components/ManageContactModal';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { handlePromise } from '~/infra/utils';
import { NewContactModel } from '~/models/ContactModel';
import { useStoreActions } from '~/store/store-context';
function calculateCanEditContact(existingContact) {
    return !!existingContact && existingContact.isEditable() && !existingContact.isSample();
}
function calculateCanEditContactV2(existingContact, allowEditContactFF) {
    if (!existingContact) {
        return false;
    }
    return allowEditContactFF && !existingContact.isSample();
}
export default function ContactDetails(props) {
    const { existingContact, refreshExistingContacts, onChange, onValidation = ()=>{}, showCustomerDetailsWarning, hideEditContact, testId, disabled } = props;
    const getProps = useStateInRef(_object_spread_props(_object_spread({}, props), {
        openContactModal,
        newContactPostActions
    }));
    const { showModal, pushBottomSheetPage, popBottomSheetPage } = useOverlayContext();
    const isInBottomSheet = useIsInBottomSheet();
    const { createContact } = useStoreActions();
    const { ALLOW_EDIT_CONTACT } = useFeatureFlags();
    const [pickerResetKey, setPickerResetKey] = useState(0);
    const [canEditContact, setCanEditContact] = useState(()=>ALLOW_EDIT_CONTACT ? calculateCanEditContactV2(existingContact, true) : calculateCanEditContact(existingContact));
    useEffect(()=>{
        const newContact = NewContactModel.makeEmpty();
        if (newContact.hasPartialContactDetails() && !existingContact) {
            handlePromise(createContact(newContact)).then(async (newContact)=>{
                if (newContact.success) {
                    await getProps().newContactPostActions(newContact.data);
                } else {
                    const savedContact = await getProps().openContactModal({
                        newContactToCreate: newContact.data
                    });
                    await getProps().newContactPostActions(savedContact);
                }
            });
        }
        setCanEditContact(ALLOW_EDIT_CONTACT ? calculateCanEditContactV2(existingContact, true) : calculateCanEditContact(existingContact));
    }, [
        existingContact,
        ALLOW_EDIT_CONTACT
    ]);
    async function openContactModal(modalProps) {
        const sharedProps = _object_spread_props(_object_spread({}, modalProps), {
            testId: 'manage-contact-modal',
            origin: 'Agreement studio'
        });
        if (isInBottomSheet) {
            return new Promise((resolve)=>{
                pushBottomSheetPage({
                    content: /*#__PURE__*/ _jsx(ManageContactModalForVendor, _object_spread_props(_object_spread({}, sharedProps), {
                        onClose: (result)=>{
                            popBottomSheetPage();
                            resolve(result);
                        }
                    }))
                });
            });
        } else {
            return showModal({
                closeOnEscapeKey: false,
                closeOnClickOutside: false,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(ManageContactModalForVendor, _object_spread_props(_object_spread({}, sharedProps), {
                        onClose: dismissModal
                    }));
                }
            });
        }
    }
    async function newContactPostActions(savedContactData) {
        if (savedContactData) {
            await refreshExistingContacts();
            onValidation(true);
            onChange({
                type: 'existingContact',
                id: savedContactData.id
            });
        } else {
            onValidation(false);
            setPickerResetKey((prev)=>prev + 1);
        }
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(GridItem, {
                span: hideEditContact ? 12 : 9,
                children: /*#__PURE__*/ _jsx(ContactPicker, {
                    label: "Primary contact",
                    disabled: disabled,
                    value: existingContact !== null && existingContact !== void 0 ? existingContact : NewContactModel.makeEmpty(),
                    onChange: async (change)=>{
                        switch(change.type){
                            case 'initial':
                                onValidation(true);
                                onChange({
                                    type: 'noContact'
                                });
                                break;
                            case 'existingContact':
                                onValidation(true);
                                onChange({
                                    type: 'existingContact',
                                    id: change.id
                                });
                                break;
                            case 'newContact':
                                {
                                    const savedContact = await openContactModal({});
                                    await newContactPostActions(savedContact);
                                    break;
                                }
                            case 'sampleContact':
                                onValidation(true);
                                onChange({
                                    type: 'sampleContact'
                                });
                                break;
                            default:
                                break;
                        }
                    },
                    testId: testId,
                    error: showCustomerDetailsWarning,
                    helperText: "Please select a contact"
                }, pickerResetKey)
            }),
            !hideEditContact && /*#__PURE__*/ _jsx(GridItem, {
                span: 3,
                children: canEditContact && /*#__PURE__*/ _jsx(TextButton, {
                    testId: "edit-contact",
                    disabled: disabled,
                    onClick: async ()=>{
                        const shouldRefresh = await openContactModal({
                            existingContact
                        });
                        if (shouldRefresh) {
                            await refreshExistingContacts();
                        }
                    },
                    children: "Edit contact"
                })
            })
        ]
    });
}
